import "ol/ol.css";
import "./OLMap.css";

import { Map, View } from "ol";
import { defaults as defaultControls, Attribution } from "ol/control";
import {
  isEmpty,
  getIntersection,
  containsXY,
  boundingExtent,
  buffer,
  getCenter,
  getWidth,
  getHeight,
  createEmpty,
  extend
} from "ol/extent";
import { defaults as defaultInteractions, Select, PinchZoom } from "ol/interaction";
import { fromLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";
import { BOUNDS } from "@/gis/ExtentConstants";
import { GISMap, OLMapCallbacks, CouncelingCenterLayerObject  } from "@/gis/GISApi";
import BasemapLayer from "@/gis/layer/Basemap";
import GeolocationLayer from '@/gis/layer/Geolocation';
import LocationLayer from "@/gis/layer/Location";
import RadiusLayer from "@/gis/layer/Radius";
import CouncelingCenterLayer from "@/gis/layer/CouncelingCenter";
import Overlay from "ol/Overlay";
import BundeslaenderLayer from "@/gis/layer/Bundeslaender";
import BezirkeLayer from "@/gis/layer/Bezirke";

const MIN_RADIUS_KM = 1.5 * 1000;  // 1.5 vs 2 in spec due to discrete zoom levels
const ANIMATION_DURATION = 300; // animation for panning
const OVERLAY_HEIGHT = 200;

export class OLMap implements GISMap {
  public map!: Map;
  public view!: View;

  private basemapLayer!: BasemapLayer;
  private bundeslaenderLayer!: BundeslaenderLayer;
  private bezirkeLayer!: BezirkeLayer;

  private radiusLayer!: RadiusLayer;
  private locationLayer!: LocationLayer;
  private councelingCenterLayer!: CouncelingCenterLayer;
  private geolocationLayer!: GeolocationLayer;

  private selectInteraction!: Select;
  private zoomedProgrammatically!: boolean;

  private isHomeSite: boolean = document && document.body && document.body.classList.contains("is-home");

  constructor(private targetElement: HTMLElement, private callbacks: OLMapCallbacks) {
    this.initMap();
  }

  setCouncelingCenters(cCenter: CouncelingCenterLayerObject[]): void {
    // console.debug("setCCs()");
    // console.debug(cCenter);
    if (this.councelingCenterLayer) {
      this.councelingCenterLayer.setCouncelingCenters(cCenter);
    }
  }
  addOverlay(overlay: Overlay): void {
    if (this.map) {
      this.map.addOverlay(overlay);
    }
  }
  zoomToCouncelingCenters(): void {
    // console.debug("zoomToCCs()");
    this.fitView(this.councelingCenterLayer);
  }

  removeCouncelingCenters(): void {
    // console.debug("removeCCs()");
    this.councelingCenterLayer.removeCouncelingCenters();
  }

  selectCouncelingCenter(cCenter: CouncelingCenterLayerObject): void {
    // console.debug("selectCC()");
    // console.debug(cCenter);
    this.councelingCenterLayer.selectCouncelingCenter(cCenter);
  }
  unselectCouncelingCenters(): void {
    // console.debug("unselectCCs()");
    this.councelingCenterLayer.unselectCouncelingCenters();
  }

  setLocation(longitude: number, latitude: number): void {
    // console.debug("setLocation()", longitude, latitude);
    this.locationLayer.setLocation(longitude, latitude);
  }

  removeLocation(): void{
    // console.debug("removeLocation()");
    this.locationLayer.removeLocation();
  }

  addGeolocation(): void {
    // console.debug("addGeolocation()");
    this.geolocationLayer.geolocation.setTracking(true);
  }

  zoomToGeolocation(): void {
    // console.debug("zoomToGeolocation()");
    this.fitView(this.geolocationLayer);
  }

  removeGeolocation(): void {
    // console.debug("removeGeolocation()");
    this.geolocationLayer.geolocation.setTracking(false);
    this.geolocationLayer.positionFeature.setGeometry(undefined);
  }

  setRadius(longitude: number, latitude: number, kmRadius: number): void {
    // console.debug("setRadius()", longitude, latitude, kmRadius);
    this.radiusLayer.setRadius(longitude, latitude, kmRadius);
  }

  zoomToRadius() {
    // console.debug("zoomToRadius()");
    this.fitView(this.radiusLayer);
  }

  removeRadius(): void{
    // console.debug("removeRadius()");
    this.radiusLayer.removeRadius();
  }

  onDestroy() {
    // console.debug("onDestroy()");
    this.map.setTarget(undefined);
  }

  onOverlayClose() {
    // console.debug("onOverlayClose()");
    this.councelingCenterLayer.unselectCouncelingCenters();
  }

  private adjustMinZoom(): void {
    if (!this.map.getSize()) {
      return;
    }
    const RESOLUTION_0 = 156543.03392811998;
    const minZoom = Math.floor(
      Math.log2(
        Math.min(
          (this.map.getSize() || [0])[0] / getWidth(BOUNDS.OE),
          (this.map.getSize() || [0, 0])[1] / getHeight(BOUNDS.OE)
        ) * RESOLUTION_0
      )
    );
    if (minZoom != this.view.getMinZoom()) {
      // console.debug("setMinZoom", this.view.getMinZoom(), "->", minZoom);
      this.view.setMinZoom(minZoom);
    }
  }

  private initMap(): void {

    if (this.isHomeSite) {
      this.bundeslaenderLayer = new BundeslaenderLayer();
      this.bezirkeLayer = new BezirkeLayer();
    } else {
      this.basemapLayer = new BasemapLayer();
    }

    this.radiusLayer = new RadiusLayer();
    this.locationLayer = new LocationLayer();
    this.councelingCenterLayer = new CouncelingCenterLayer();
    this.geolocationLayer = new GeolocationLayer();

    this.selectInteraction = this.councelingCenterLayer.getSelectInteraction();

    this.zoomedProgrammatically = false;

    this.selectInteraction.on("select", (e: any) => {

      this.zoomedProgrammatically = false;

      if (e.selected.length) {

        // Cluster clicked
        if (e.selected[0].get("features").length > 1 && !this.isHomeSite) {
          this.zoomedProgrammatically = true;
          this.map.forEachFeatureAtPixel(e.mapBrowserEvent.pixel, (feature) => {
            const featuresAtPixel = feature.get('features');
            if (featuresAtPixel && featuresAtPixel.length > 1) {
              // Calculate the extent of the cluster members.
              const extent = createEmpty();
              featuresAtPixel.forEach((feature: any) =>
                extend(extent, feature.getGeometry().getExtent())
              );
              const view = e.mapBrowserEvent.target.getView();
              // const resolution = view.getResolution();
              if (
                view.getZoom() === view.getMaxZoom()
                // || (getWidth(extent) < resolution)
              ) {
                const filteredcCenters = feature.get('features').map( (f: Feature<any>) => {
                  return f.get("cCenter")
                })

                this.callbacks.showCouncelingCenterOverlay( filteredcCenters, e );
              } else {
                // Zoom to the extent of the cluster members.
                view.fit(extent, {duration: 500, padding: [50, 50, 50, 50]});
              }
            }
          })
        } else {
          this.zoomedProgrammatically = true;
          const cCenter = Object.assign({}, e.selected[0].getProperties().features[0].getProperties().cCenter);
          const coordinate = fromLonLat([cCenter.longitude, cCenter.latitude]);

          const resolution = this.map.getView().getResolution() || 0;
          const extent = this.map.getView().calculateExtent();
          extent[3] -= (resolution as any) * OVERLAY_HEIGHT; // overlay offset unterer Bereich abziehen

          if ( !containsXY(extent, coordinate[0], coordinate[1]) ) {
            this.map.getView().fit(
              buffer(boundingExtent([coordinate]), resolution * 10)
              , {
                minResolution: resolution,
                duration: ANIMATION_DURATION,
                padding: [100,0,0,0]
                /*
                maxZoom: this.map.getView().getZoom(),
                padding: [OVERLAY_HEIGHT * 2, 0, 0, 0] // top, right, bottom and left padding
                */
              }
            );
          }
          this.callbacks.showCouncelingCenterOverlay( [cCenter], e );
        }

      } else {
        this.callbacks.showCouncelingCenterOverlay( [], e );
      }
    });

    let layers = [];
    if (this.isHomeSite) {
      layers = [this.bundeslaenderLayer, this.bezirkeLayer, this.geolocationLayer, this.radiusLayer, this.locationLayer, this.councelingCenterLayer];
    } else {
      const tileLayer = this.basemapLayer.getTileLayer();
      layers = [tileLayer, this.basemapLayer, this.geolocationLayer, this.radiusLayer, this.locationLayer, this.councelingCenterLayer];
    }

    this.map = new Map({
      controls: defaultControls({ attribution: false }).extend([new Attribution({ collapsible: false })]),
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false
      }).extend([ this.selectInteraction, new PinchZoom() ]),
      layers,
      target: this.targetElement,
    });

    this.map.on('pointermove', evt => {
      if ((evt as any).dragging) {
        return;
      }
      const pixel = this.map.getEventPixel((evt as any).originalEvent);
      const hit = this.map.forEachFeatureAtPixel(pixel, function(feature, layer) {
        if (layer instanceof CouncelingCenterLayer) {
          return true;
        }
      });
      if (hit) {
        (this.map.getTargetElement() as HTMLElement).style.cursor = 'pointer';
      } else {
        (this.map.getTargetElement() as HTMLElement).style.cursor = '';
      }
    });

    this.view = new View({
      center: getCenter( BOUNDS.OE ),
      constrainOnlyCenter: true,
      constrainResolution: true, // discrete zooomlevels
      extent: BOUNDS.OE,
      maxZoom: 18,
      minZoom: 0,
      zoom: 7,
    });

    this.map.setView(this.view);
    this.map.on('change:size', () => this.adjustMinZoom());

    this.geolocationLayer.initGeolocation(this.view.getProjection());

    this.adjustMinZoom();
    this.view.setZoom(this.view.getMinZoom());

    // Detect movement of map and display Button "in diesem bereich suchen"
    setTimeout(() => {
      this.map.on('moveend', () => {
        // Check if move was programmatically or not in order to show searchbutton
        if (!this.zoomedProgrammatically) {
          this.callbacks.showAreaSearchButton(true)
        } else {
          this.callbacks.showAreaSearchButton(false)
        }

        this.zoomedProgrammatically = false;
      });
    }, 2000)
  }

  private fitView(layer: VectorLayer<any>) {

    this.zoomedProgrammatically = true;

    let extent = layer.getSource().getSource().getExtent();
    extent = getIntersection(extent, BOUNDS.OE);  // ensure to be in AT

    if (!isEmpty(extent) && ( getWidth(extent) < 2 * MIN_RADIUS_KM || getHeight(extent) < 2 * MIN_RADIUS_KM ) ) {
      const bufferToAdd = MIN_RADIUS_KM - Math.min(getWidth(extent), getHeight(extent)) / 2;
      extent = buffer(extent, bufferToAdd);
    }

    if (!isEmpty (extent)) {
      this.map.getView().fit(
        extent
        , {
          maxZoom: 18,
          duration: ANIMATION_DURATION,
          padding: [0 + 25, 40 + 10, 30 + 5, 0 + 10] // top, right, bottom and left padding
        }
      );
    }

  }

}
